import React, {useCallback, useRef, useState} from "react";
import {message} from "antd";
import {ScheduleConflictModal} from "./ScheduleConflictModal";
import {
  EMAIL_AUTOMATION_SAVE_ERROR_FALLBACK,
  getApiErrorMessage,
  parseScheduleConflictError,
  type ScheduleConflictDetails,
} from "./email-automation-schedule-conflict";

/**
 * Shared save-error UI for email automations.
 * When a schedule conflict is detected, optionally closes the automation sheet first
 * so the conflict modal is not obscured by the drawer.
 */
export function useEmailAutomationSaveErrorUI(onScheduleConflict?: () => void) {
  const [conflictDetails, setConflictDetails] = useState<ScheduleConflictDetails | null>(
    null
  );
  const [isConflictModalOpen, setIsConflictModalOpen] = useState(false);
  const onScheduleConflictRef = useRef(onScheduleConflict);
  onScheduleConflictRef.current = onScheduleConflict;

  const closeConflictModal = useCallback(() => {
    setIsConflictModalOpen(false);
    setConflictDetails(null);
  }, []);

  const handleSaveError = useCallback((err: unknown) => {
    try {
      const conflict = parseScheduleConflictError(err);
      if (conflict) {
        onScheduleConflictRef.current?.();
        setConflictDetails(conflict);
        setIsConflictModalOpen(true);
        return;
      }

      const apiMessage = getApiErrorMessage(err);
      message.error(apiMessage || EMAIL_AUTOMATION_SAVE_ERROR_FALLBACK);
    } catch {
      const apiMessage = getApiErrorMessage(err);
      message.error(apiMessage || EMAIL_AUTOMATION_SAVE_ERROR_FALLBACK);
    }
  }, []);

  const ScheduleConflictModalElement = (
    <ScheduleConflictModal
      open={isConflictModalOpen}
      onClose={closeConflictModal}
      details={conflictDetails}
    />
  );

  return {
    handleSaveError,
    ScheduleConflictModal: ScheduleConflictModalElement,
  };
}
